import React, {useCallback, useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {
  ConsentCategorySource,
  ConsentClinicalSigningMode,
  ConsentDocument,
  ConsentDocumentActiveStage,
  ConsentDocumentAuditEvent,
  ConsentDocumentAuditEventType,
  ConsentDocumentSignature,
  ConsentDocumentSignatory,
  ConsentDocumentStatus,
  ConsentSignerRole,
  ConsentSigningMethod,
  ConsentSignatoryState,
  ConsentUserSummary,
  ConsentWithdrawalRequest,
  ConsentWithdrawalRequestStatus,
} from "@source/api/models/consentModels";
import {
  formatConsentFileSize,
  formatIdentityVerificationMethod,
  getConsentAuditEventLabel,
  getConsentSignerRoleLabel,
  getConsentSigningMethodLabel,
  formatEmployeeSignerRoles,
  getFamilyContactTypeLabel,
} from "@source/api/models/consentHelpers";
import Button from "@source/components/UI/buttons/button/button";
import CacheImg from "@source/components/UI/cacheImg/cacheImg";
import ConfirmModal from "@source/components/confirmModal/confirmModal";
import Modal from "@source/components/UI/modal/modal";
import TextInput from "@source/components/UI/inputs/textInput/textInput";
import Notification from "@source/components/snackMessage/notification";
import {employeeSignPath} from "@source/api/models/consentHelpers";
import {canManageConsentDocuments} from "@source/utils/consentAccess";
import userStore from "@source/store/userStore";
import {stringToHslColor} from "@source/utils/utils";
import {ConsentRequestState, ConsentStatusLabel, SignatoryProgress} from "@source/components/consentDocuments";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import {canCurrentEmployeeSign, employeeSignAccessFromUser} from "@source/components/consentDocuments/employeeSigningHelpers";
import styles from "./consentDocumentDetail.module.scss";

const personName = (person?: Pick<ConsentUserSummary, "firstName" | "lastName">) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || "Unavailable";

const sourceLabel = (source?: ConsentCategorySource) =>
  source === ConsentCategorySource.TEMPLATE ? "Template" : source === ConsentCategorySource.RAW_PDF ? "Existing PDF" : "Unavailable";

const hasFinalPdf = (status?: ConsentDocumentStatus) =>
  status === ConsentDocumentStatus.COMPLETED || status === ConsentDocumentStatus.DECLINED || status === ConsentDocumentStatus.WITHDRAWN;

const WITHDRAWAL_TEXT_MAX = 2000;
const MANUAL_REMINDERS_PER_HOUR = 3;

const signerHasSigned = (document: ConsentDocument, signerId: string, signerType: ConsentSignerRole) =>
  (document.signatures || []).some((signature) => signature.signerId === signerId && signature.signerType === signerType);

const manualReminderEvents = (document: ConsentDocument, recipientId: string, signerType: ConsentSignerRole) =>
  (document.auditEvents || []).filter((event) =>
    event.type === ConsentDocumentAuditEventType.REMINDER_SENT
    && event.signerType === signerType
    && event.recipientId === recipientId
    && Boolean(event.actorId));

const manualRemindersInLastHour = (document: ConsentDocument, recipientId: string, signerType: ConsentSignerRole) => {
  const windowStart = Date.now() - 60 * 60 * 1000;
  return manualReminderEvents(document, recipientId, signerType)
    .filter((event) => new Date(event.occurredAt).getTime() >= windowStart).length;
};

const latestManualReminder = (document: ConsentDocument, recipientId: string, signerType: ConsentSignerRole) =>
  manualReminderEvents(document, recipientId, signerType)
    .sort((left, right) => new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime())[0];

const reminderStageActive = (document: ConsentDocument, signerType: ConsentSignerRole) => {
  const stages = document.activeStages;
  if (!Array.isArray(stages)) return false;
  if (signerType === ConsentSignerRole.FAMILY_MEMBER) return stages.includes(ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT);
  if (signerType === ConsentSignerRole.EMPLOYEE) return stages.includes(ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES);
  if (signerType === ConsentSignerRole.GP_CLINICAL) return stages.includes(ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE);
  return false;
};

const canRemindSigner = (document: ConsentDocument, signatory: Pick<ConsentDocumentSignatory, "id" | "role" | "signingMode" | "state" | "signed">) => {
  if (!signatory.id || signatory.signed || signatory.state !== ConsentSignatoryState.ACTIVE || !canManageConsentDocuments()) return false;
  if (document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES || !reminderStageActive(document, signatory.role)) return false;
  if (signerHasSigned(document, signatory.id, signatory.role)) return false;
  if (signatory.role === ConsentSignerRole.FAMILY_MEMBER) {
    return signatory.signingMode !== ConsentClinicalSigningMode.ON_SITE && document.familySigningMode !== ConsentClinicalSigningMode.ON_SITE;
  }
  if (signatory.role === ConsentSignerRole.EMPLOYEE) return true;
  if (signatory.role === ConsentSignerRole.GP_CLINICAL) {
    return document.clinicalSigningMode === ConsentClinicalSigningMode.BRENNA_USER && signatory.signingMode !== ConsentClinicalSigningMode.ON_SITE;
  }
  return false;
};

const errorDescription = (error: unknown, action: "document" | "PDF") => {
  const status = error instanceof ReqError ? error.status : undefined;
  if (status === 403) return `You do not have permission to view this ${action}.`;
  if (status === 404) return action === "PDF" ? "The final PDF is no longer available." : "This consent document could not be found for the selected nursing home.";
  if (status === 409) return action === "PDF" ? "The final PDF is not available because the document is not final or its integrity check failed." : "This consent document is not available in its current workflow state.";
  return error instanceof Error ? error.message : `Unable to load this ${action}.`;
};

const ResidentPhoto = ({document}: {document: ConsentDocument}) => {
  const residentName = personName(document.resident);
  const initials = `${document.resident?.firstName?.[0] || ""}${document.resident?.lastName?.[0] || ""}`.toUpperCase() || "R";
  return <CacheImg url={document.resident?.photo} alt={`${residentName} avatar`} className={styles.photo} imgClassName={styles.photo} skeletonProps={{variant: "circular"}}>
    <div className={styles.noPhoto} style={{backgroundColor: stringToHslColor(`${document.resident?.firstName || ""}${document.resident?.lastName || ""}`)}}>{initials}</div>
  </CacheImg>;
};

const SignatureEvidence = ({signature, hideBrennaId}: {signature: ConsentDocumentSignature; hideBrennaId: boolean}) => (
  <li className={styles.evidenceRow}>
    <div><strong>{signature.signerName || "Signer unavailable"}</strong><span>{getConsentSignerRoleLabel(signature.signerType)}</span></div>
    <dl>
      {hideBrennaId ? null : <><dt>Brenna ID</dt><dd>{signature.signerId}</dd></>}
      {signature.signingMethod ? <><dt>Signing method</dt><dd>{getConsentSigningMethodLabel(signature.signingMethod)}</dd></> : null}
      <dt>Acceptance statement</dt><dd>{signature.acceptanceStatement}</dd>
      <dt>Signed</dt><dd>{formatConsentDate(signature.signedAt)}</dd>
      {signature.employeePosition ? <><dt>Position</dt><dd>{signature.employeePosition}</dd></> : null}
      {signature.facilitatedByEmployeeName ? <><dt>Facilitated by</dt><dd>{signature.facilitatedByEmployeeName}{signature.facilitatedByEmployeePosition ? ` (${signature.facilitatedByEmployeePosition})` : ""}</dd></> : null}
      {signature.familyRelationship ? <><dt>Relationship</dt><dd>{signature.familyRelationship}</dd></> : null}
      {signature.familyContactType ? <><dt>Contact type</dt><dd>{getFamilyContactTypeLabel(signature.familyContactType)}</dd></> : null}
    </dl>
  </li>
);

const renewalMailEvent = (type: ConsentDocumentAuditEvent["type"]) =>
  type === ConsentDocumentAuditEventType.EXPIRY_REMINDER_SENT
  || type === ConsentDocumentAuditEventType.REVIEW_REMINDER_SENT
  || type === ConsentDocumentAuditEventType.EXPIRED
  || type === ConsentDocumentAuditEventType.REVIEW_OVERDUE;

const AuditEvidence = ({event}: {event: ConsentDocumentAuditEvent}) => {
  const signingReminder = event.type === ConsentDocumentAuditEventType.REMINDER_SENT || event.type === ConsentDocumentAuditEventType.WITHDRAWAL_REMINDER_SENT;
  const showMail = signingReminder || (renewalMailEvent(event.type) && Boolean(event.recipientName || event.mailRecordId || event.channel));
  return (
    <li className={styles.timelineRow}>
      <div className={styles.timelineDot} aria-hidden="true"/>
      <div>
        <strong>{getConsentAuditEventLabel(event)}</strong>
        <time dateTime={event.occurredAt}>{formatConsentDate(event.occurredAt)}</time>
        {showMail ? <span className={styles.eventDetail}>
          Recipient: {event.recipientName || "Unavailable"}{event.recipientId ? ` (Brenna ID: ${event.recipientId})` : ""}{event.signerType ? ` · ${getConsentSignerRoleLabel(event.signerType)}` : ""}{event.channel ? ` · ${event.channel}` : ""}{event.reminderNumber ? ` · Reminder ${event.reminderNumber}` : ""}{event.mailRecordId ? ` · Mail record ID: ${event.mailRecordId}` : ""}
        </span> : event.actorId ? <span className={styles.eventDetail}>Actor Brenna ID: {event.actorId}</span> : null}
      </div>
    </li>
  );
};

export const ConsentDocumentDetail = ({nursingHomeId}: {nursingHomeId: string}) => {
  const {documentId} = useParams<{documentId: string}>();
  const [document, setDocument] = useState<ConsentDocument>();
  const [error, setError] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [pdfUnavailable, setPdfUnavailable] = useState(false);
  const [finalPdfUrl, setFinalPdfUrl] = useState("");
  const [withdrawalModal, setWithdrawalModal] = useState(false);
  const [withdrawalText, setWithdrawalText] = useState("");
  const [withdrawalError, setWithdrawalError] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [remindingId, setRemindingId] = useState("");
  const [reminderError, setReminderError] = useState("");
  const requestIdRef = useRef(0);
  const finalPdfRequestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const finalPdfUrlRef = useRef<string>();

  const revokeFinalPdfUrl = useCallback(() => {
    if (finalPdfUrlRef.current) {
      URL.revokeObjectURL(finalPdfUrlRef.current);
      finalPdfUrlRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      revokeFinalPdfUrl();
    };
  }, [revokeFinalPdfUrl]);

  useEffect(() => {
    finalPdfRequestIdRef.current += 1;
    revokeFinalPdfUrl();
    setFinalPdfUrl("");
    setPdfLoading(false);
    setPdfError("");
    setPdfUnavailable(false);
  }, [documentId, revokeFinalPdfUrl]);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    setAccessDenied(false);
    try {
      const nextDocument = await ConsentController.getDocument(nursingHomeId, documentId);
      if (mountedRef.current && requestId === requestIdRef.current) setDocument(nextDocument);
    } catch (requestError: unknown) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setDocument(undefined);
        const status = requestError instanceof ReqError ? requestError.status : undefined;
        if (status === 403) setAccessDenied(true);
        else setError(errorDescription(requestError, "document"));
      }
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, [documentId, nursingHomeId]);

  useEffect(() => {
    load();
    return () => { requestIdRef.current += 1; };
  }, [load]);

  const openFinalPdf = async () => {
    const requestId = ++finalPdfRequestIdRef.current;
    setPdfLoading(true);
    setPdfError("");
    try {
      const bytes = await ConsentController.getFinalPdf(documentId);
      const url = URL.createObjectURL(new Blob([bytes], {type: "application/pdf"}));
      if (!mountedRef.current || requestId !== finalPdfRequestIdRef.current) {
        URL.revokeObjectURL(url);
        return;
      }
      revokeFinalPdfUrl();
      finalPdfUrlRef.current = url;
      setFinalPdfUrl(url);
    } catch (requestError: unknown) {
      if (mountedRef.current && requestId === finalPdfRequestIdRef.current) {
        const status = requestError instanceof ReqError ? requestError.status : undefined;
        if (status === 409 && document?.status === ConsentDocumentStatus.DECLINED) setPdfUnavailable(true);
        else setPdfError(errorDescription(requestError, "PDF"));
      }
    } finally {
      if (mountedRef.current && requestId === finalPdfRequestIdRef.current) setPdfLoading(false);
    }
  };

  const clearFinalPdf = () => {
    finalPdfRequestIdRef.current += 1;
    revokeFinalPdfUrl();
    setFinalPdfUrl("");
    setPdfLoading(false);
    setPdfError("");
    setPdfUnavailable(false);
  };

  const refreshAfterConflict = async () => {
    try {
      const nextDocument = await ConsentController.getDocument(nursingHomeId, documentId);
      if (mountedRef.current) setDocument(nextDocument);
    } catch {
      if (mountedRef.current) void load();
    }
  };

  const openWithdrawalModal = () => {
    const pending = document?.withdrawalRequests?.find((request) => request.status === ConsentWithdrawalRequestStatus.PENDING);
    setWithdrawalText(pending?.reason || "");
    setWithdrawalError("");
    setWithdrawalModal(true);
  };

  const submitWithdrawal = async () => {
    const text = withdrawalText.trim();
    if (!text) return;
    setWithdrawing(true);
    setWithdrawalError("");
    try {
      const nextDocument = await ConsentController.withdrawDocument(nursingHomeId, documentId, {reason: text});
      if (!mountedRef.current) return;
      setDocument(nextDocument);
      if (nextDocument.status === ConsentDocumentStatus.WITHDRAWN) clearFinalPdf();
      setWithdrawalModal(false);
    } catch (requestError: unknown) {
      if (!mountedRef.current) return;
      const status = requestError instanceof ReqError ? requestError.status : undefined;
      setWithdrawalError(requestError instanceof Error ? requestError.message : "Unable to update this consent document.");
      if (status === 409) {
        setWithdrawalModal(false);
        await refreshAfterConflict();
      }
    } finally {
      if (mountedRef.current) setWithdrawing(false);
    }
  };

  const sendReminder = async (recipientId: string, signerType: ConsentSignerRole) => {
    setRemindingId(recipientId);
    setReminderError("");
    try {
      if (signerType === ConsentSignerRole.EMPLOYEE) {
        await ConsentController.remindEmployee(nursingHomeId, documentId, recipientId);
      } else if (signerType === ConsentSignerRole.GP_CLINICAL) {
        await ConsentController.remindClinicalSigner(nursingHomeId, documentId);
      } else {
        await ConsentController.remindFamilyMember(nursingHomeId, documentId, recipientId);
      }
    } catch (requestError: unknown) {
      if (!mountedRef.current) return;
      const status = requestError instanceof ReqError ? requestError.status : undefined;
      setReminderError(requestError instanceof Error ? requestError.message : "Unable to send this reminder.");
      if (status === 409 || status === 429) await refreshAfterConflict();
      if (mountedRef.current) setRemindingId("");
      return;
    }
    if (!mountedRef.current) return;
    setDocument((current) => current ? {
      ...current,
      auditEvents: [...(current.auditEvents || []), {
        type: ConsentDocumentAuditEventType.REMINDER_SENT,
        actorId: userStore.getId(),
        occurredAt: new Date().toISOString(),
        recipientId,
        signerType,
        channel: "email",
      }],
    } : current);
    try {
      const nextDocument = await ConsentController.getDocument(nursingHomeId, documentId);
      if (mountedRef.current && nextDocument) setDocument(nextDocument);
    } catch {
      if (mountedRef.current) setReminderError("Reminder sent, but the page could not be refreshed.");
    } finally {
      if (mountedRef.current) {
        setRemindingId("");
        new Notification().success("Reminder sent.");
      }
    }
  };

  const cancelWithdrawalRequest = async () => {
    setWithdrawing(true);
    setWithdrawalError("");
    try {
      const nextDocument = await ConsentController.closeWithdrawalRequest(nursingHomeId, documentId, {});
      if (mountedRef.current) setDocument(nextDocument);
    } catch (requestError: unknown) {
      if (!mountedRef.current) return;
      const status = requestError instanceof ReqError ? requestError.status : undefined;
      setWithdrawalError(requestError instanceof Error ? requestError.message : "Unable to cancel this withdrawal request.");
      if (status === 409) await refreshAfterConflict();
    } finally {
      if (mountedRef.current) setWithdrawing(false);
    }
  };

  if (loading) return <ConsentRequestState variant="loading" title="Loading consent document"/>;
  if (accessDenied) return <ConsentRequestState variant="error" title="No access" description="You do not have permission to view this consent document."/>;
  if (error) return <ConsentRequestState variant="error" title="Unable to load consent document" description={error} onRetry={load}/>;
  if (!document) return null;

  const categoryName = document.categorySnapshot?.name || "Legacy consent document";
  const familySigners = document.familyMembers?.length ? document.familyMembers : document.familyMember ? [document.familyMember] : [];
  const canSign = canCurrentEmployeeSign(document, userStore.getId(), employeeSignAccessFromUser(userStore.getUserInfo()));
  const requiredRoles = document.signatories
    ? document.signatories.map((signatory) => signatory.role)
    : document.categorySnapshot?.requiredSignerRoles || [];
  const needsResidentIdentity = requiredRoles.includes(ConsentSignerRole.RESIDENT) || Boolean(document.residentIdentityVerification);
  const needsClinicalIdentity = (requiredRoles.includes(ConsentSignerRole.GP_CLINICAL) && document.clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE) || Boolean(document.clinicalIdentityVerification);
  const needsFamilyIdentity = document.familySigningMode === ConsentClinicalSigningMode.ON_SITE || Boolean(document.familyIdentityVerification);
  const pendingRequest = document.withdrawalRequests?.find((request) => request.status === ConsentWithdrawalRequestStatus.PENDING);
  const canManageWithdrawal = canManageConsentDocuments() && document.status === ConsentDocumentStatus.COMPLETED;
  const resolvedRequests = (document.withdrawalRequests || []).filter((request) =>
    request.status !== ConsentWithdrawalRequestStatus.PENDING && request.id !== document.withdrawal?.requestId);
  const linkedRequest = document.withdrawal?.requestId
    ? document.withdrawalRequests?.find((request) => request.id === document.withdrawal?.requestId)
    : undefined;
  const requestByline = (request: ConsentWithdrawalRequest) =>
    `${request.requestedByName}${request.familyRelationship ? ` (${request.familyRelationship})` : ""} on ${formatConsentDate(request.requestedAt)}`;
  const reminderControl = (recipientId: string, signerType: ConsentSignerRole) => {
    const latestReminder = latestManualReminder(document, recipientId, signerType);
    const reminderLimitReached = manualRemindersInLastHour(document, recipientId, signerType) >= MANUAL_REMINDERS_PER_HOUR;
    return <span className={styles.reminderActions} title={reminderLimitReached ? "This person has already received 3 reminders in the last hour." : undefined}>
      {latestReminder ? <span className={styles.reminderNote}>Reminder sent {formatConsentDate(latestReminder.occurredAt)}</span> : null}
      <Button size="small" className={styles.remindBtn} onClick={() => sendReminder(recipientId, signerType)} disabled={reminderLimitReached || Boolean(remindingId)} isLoading={remindingId === recipientId}>Send reminder</Button>
    </span>;
  };

  return <div className={styles.detail}>
    <section className={styles.summary} aria-label="Consent document summary">
      <ResidentPhoto document={document}/>
      <div className={styles.summaryContent}>
        <div className={styles.summaryHeading}><h2>{document.title || "Untitled consent document"}</h2></div>
        <p>{personName(document.resident)} · {categoryName} · {sourceLabel(document.categorySnapshot?.source || document.source)}</p>
        <dl className={styles.summaryMeta}>
          <div><dt>Created</dt><dd>{formatConsentDate(document.createdAt)}</dd></div>
          <div><dt>Sent</dt><dd>{document.sentAt ? formatConsentDate(document.sentAt) : "Not sent"}</dd></div>
          {document.reviewDueAt ? <div><dt>Review date</dt><dd>{formatConsentDate(document.reviewDueAt)}</dd></div> : null}
          {document.expiresAt ? <div><dt>Expiry date</dt><dd>{formatConsentDate(document.expiresAt)}</dd></div> : null}
          <div><dt>Created by</dt><dd>{personName(document.createdBy)}</dd></div>
        </dl>
      </div>
      <div className={styles.summaryAside}>
        <ConsentStatusLabel status={document.status} displayStatus={document.displayStatus} activeStages={document.activeStages} clinicalSigningMode={document.clinicalSigningMode} familySigningMode={document.familySigningMode} signatories={document.signatories}/>
        {canSign || (canManageWithdrawal && !pendingRequest) ? <div className={styles.summaryActions}>
          {canManageWithdrawal && !pendingRequest ? <Button size="small" variant="outlined" className={styles.actionBtn} onClick={openWithdrawalModal} disabled={withdrawing}>Withdraw consent</Button> : null}
          {canSign ? <Button size="small" event="link" link={employeeSignPath(document._id)} className={styles.actionBtn}>Sign document</Button> : null}
        </div> : null}
        {withdrawalError && !withdrawalModal && !pendingRequest ? <p className={styles.pdfError} role="alert">{withdrawalError}</p> : null}
      </div>
    </section>

    {pendingRequest ? <section className={styles.withdrawalBanner} aria-label="Withdrawal requested">
      <div className={styles.withdrawalBannerHeader}>
        <div>
          <h3>Withdrawal requested</h3>
          <p className={styles.withdrawalByline}>{requestByline(pendingRequest)}</p>
        </div>
        {canManageWithdrawal ? <div className={styles.withdrawalActions}>
          <ConfirmModal title="Cancel withdrawal request?" confirmCb={cancelWithdrawalRequest} confirmLoading={withdrawing} confirmBtnInner="Cancel request" rejectBtnInner="Back">
            <Button size="small" variant="outlined" className={styles.actionBtn} disabled={withdrawing}>Cancel request</Button>
          </ConfirmModal>
          <Button size="small" className={styles.actionBtn} onClick={openWithdrawalModal} disabled={withdrawing}>Withdraw consent</Button>
        </div> : null}
      </div>
      <blockquote className={styles.withdrawalReason}>{pendingRequest.reason}</blockquote>
      {withdrawalError && !withdrawalModal ? <p className={styles.pdfError} role="alert">{withdrawalError}</p> : null}
    </section> : null}

    <section className={styles.section}><h3>Document file</h3>
      {hasFinalPdf(document.status) ? <>
        {pdfUnavailable ? <p className={styles.absent}>PDF not available</p> : <>
          <div className={styles.fileRow}>
            <p className={styles.fileDescription}>{document.status === ConsentDocumentStatus.WITHDRAWN ? "The withdrawn document is available as a PDF." : document.status === ConsentDocumentStatus.DECLINED ? "The final document is available as a PDF." : "The final signed document is available as a PDF."}</p>
            {!finalPdfUrl
              ? <Button size="small" className={styles.actionBtn} onClick={openFinalPdf} disabled={pdfLoading} isLoading={pdfLoading}>View final PDF</Button>
              : <Button size="small" variant="outlined" className={styles.actionBtn} onClick={() => window.open(finalPdfUrl, "_blank", "noopener,noreferrer")}>Open in new tab</Button>}
          </div>
          {finalPdfUrl ? <iframe className={styles.finalPdfFrame} src={finalPdfUrl} title="Final consent PDF"/> : null}
          {pdfError ? <p className={styles.pdfError} role="alert">{pdfError}</p> : null}
        </>}
      </> : <ConsentDocumentPreview documentId={document._id}/>}
    </section>

    {document.supportingDocuments?.length ? <section className={styles.section} aria-label="Supporting Documents">
      <h3>Supporting Documents</h3>
      <ul className={styles.supportingList}>
        {document.supportingDocuments.map((file) => <li className={styles.fileRow} key={file._id}>
          <div className={styles.supportingFile}>
            <strong>{file.originalFileName}</strong>
            <span>{formatConsentFileSize(file.size)} · Uploaded {formatConsentDate(file.uploadedAt)}</span>
          </div>
          {file.url
            ? <Button size="small" variant="outlined" className={styles.actionBtn} onClick={() => window.open(file.url, "_blank", "noopener,noreferrer")}>View</Button>
            : <span className={styles.absent}>Refresh this document to view the file.</span>}
        </li>)}
      </ul>
    </section> : null}

    <section className={styles.section}><h3>Assignments and signing progress</h3>
      {document.signatories?.length ? <SignatoryProgress signatories={document.signatories} employeeSignerRoles={document.categorySnapshot?.employeeSignerRoles} renderPersonAction={(signatory) => {
        if (!canRemindSigner(document, signatory) || !signatory.id) return null;
        return reminderControl(signatory.id, signatory.role);
      }}/> : <div className={styles.assignmentList}>
        {document.requiredEmployeeSigners?.length
          ? document.requiredEmployeeSigners.map((person) => <span className={styles.assignmentIdentity} key={person._id}>
            <span>Employee: {personName(person)}</span>
            {canRemindSigner(document, {id: person._id, role: ConsentSignerRole.EMPLOYEE, state: ConsentSignatoryState.ACTIVE, signed: false}) ? reminderControl(person._id, ConsentSignerRole.EMPLOYEE) : null}
          </span>)
          : document.categorySnapshot?.employeeSignerRoles?.length
            ? <span>Employee: {formatEmployeeSignerRoles(document.categorySnapshot.employeeSignerRoles)}</span>
            : null}
        {document.clinicalSigner ? <span className={styles.assignmentIdentity}>
          <span>GP / Clinician: {personName(document.clinicalSigner)}</span>
          {canRemindSigner(document, {id: document.clinicalSigner._id, role: ConsentSignerRole.GP_CLINICAL, signingMode: document.clinicalSigningMode, state: ConsentSignatoryState.ACTIVE, signed: false}) ? reminderControl(document.clinicalSigner._id, ConsentSignerRole.GP_CLINICAL) : null}
        </span> : document.clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE ? <span>GP / Clinician: GP / Clinician (on-site)</span> : null}
        {document.familySigningMode === ConsentClinicalSigningMode.ON_SITE ? <span>Family member: {document.familyIdentityVerification?.signerName || "Family member (on-site)"}</span> : familySigners.map((person) => <span className={styles.assignmentIdentity} key={person._id}>
          <span>Family member: {personName(person)}</span>
          {canRemindSigner(document, {id: person._id, role: ConsentSignerRole.FAMILY_MEMBER, signingMode: document.familySigningMode, state: ConsentSignatoryState.ACTIVE, signed: false}) ? reminderControl(person._id, ConsentSignerRole.FAMILY_MEMBER) : null}
        </span>)}
        {document.resident ? <span>Resident: {personName(document.resident)}</span> : null}
        {!document.requiredEmployeeSigners?.length && !document.categorySnapshot?.employeeSignerRoles?.length && !familySigners.length && !document.resident ? <span>Signing details unavailable</span> : null}
      </div>}
      {reminderError ? <p className={styles.pdfError} role="alert">{reminderError}</p> : null}
    </section>

    <section className={styles.section}><h3>Signatures</h3>
      {document.signatures?.length ? <ul className={styles.evidenceList}>{document.signatures.map((signature, index) => <SignatureEvidence key={`${signature.signerId}-${signature.signedAt}-${index}`} signature={signature} hideBrennaId={(document.clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE && signature.signerType === ConsentSignerRole.GP_CLINICAL) || signature.signingMethod === ConsentSigningMethod.FAMILY_ON_SITE || (document.familySigningMode === ConsentClinicalSigningMode.ON_SITE && signature.signerType === ConsentSignerRole.FAMILY_MEMBER)}/>)}</ul> : <p className={styles.absent}>No signatures have been recorded.</p>}
    </section>

    {needsResidentIdentity ? <section className={styles.section}><h3>Resident identity verification</h3>
      {document.residentIdentityVerification ? <div className={styles.evidenceRow}><dl>
        <dt>Verifier</dt><dd>{document.residentIdentityVerification.verifiedByEmployeeName}</dd>
        <dt>Brenna ID</dt><dd>{document.residentIdentityVerification.verifiedByEmployeeId}</dd>
        {document.residentIdentityVerification.verifiedByEmployeePosition ? <><dt>Position</dt><dd>{document.residentIdentityVerification.verifiedByEmployeePosition}</dd></> : null}
        <dt>Statement</dt><dd>{document.residentIdentityVerification.identityVerificationStatement}</dd>
        <dt>Verified</dt><dd>{formatConsentDate(document.residentIdentityVerification.verifiedAt)}</dd>
      </dl></div> : <p className={styles.absent}>Resident identity has not been verified.</p>}
    </section> : null}

    {needsClinicalIdentity ? <section className={styles.section}><h3>GP / Clinician identity verification</h3>
      {document.clinicalIdentityVerification ? <div className={styles.evidenceRow}><dl>
        <dt>GP / Clinician</dt><dd>{document.clinicalIdentityVerification.signerName}</dd>
        <dt>Method</dt><dd>{formatIdentityVerificationMethod(document.clinicalIdentityVerification)}</dd>
        <dt>Verifier</dt><dd>{document.clinicalIdentityVerification.verifiedByEmployeeName}</dd>
        <dt>Brenna ID</dt><dd>{document.clinicalIdentityVerification.verifiedByEmployeeId}</dd>
        {document.clinicalIdentityVerification.verifiedByEmployeePosition ? <><dt>Position</dt><dd>{document.clinicalIdentityVerification.verifiedByEmployeePosition}</dd></> : null}
        <dt>Statement</dt><dd>{document.clinicalIdentityVerification.identityVerificationStatement}</dd>
        <dt>Verified</dt><dd>{formatConsentDate(document.clinicalIdentityVerification.verifiedAt)}</dd>
      </dl></div> : <p className={styles.absent}>GP / Clinician identity has not been verified.</p>}
    </section> : null}

    {needsFamilyIdentity ? <section className={styles.section}><h3>Family member identity verification</h3>
      {document.familyIdentityVerification ? <div className={styles.evidenceRow}><dl>
        <dt>Family member</dt><dd>{document.familyIdentityVerification.signerName}</dd>
        <dt>Relationship</dt><dd>{document.familyIdentityVerification.relationship}</dd>
        <dt>Contact type</dt><dd>{getFamilyContactTypeLabel(document.familyIdentityVerification.contactType)}</dd>
        <dt>Method</dt><dd>{formatIdentityVerificationMethod(document.familyIdentityVerification)}</dd>
        <dt>Verifier</dt><dd>{document.familyIdentityVerification.verifiedByEmployeeName}</dd>
        <dt>Brenna ID</dt><dd>{document.familyIdentityVerification.verifiedByEmployeeId}</dd>
        {document.familyIdentityVerification.verifiedByEmployeePosition ? <><dt>Position</dt><dd>{document.familyIdentityVerification.verifiedByEmployeePosition}</dd></> : null}
        <dt>Statement</dt><dd>{document.familyIdentityVerification.identityVerificationStatement}</dd>
        <dt>Verified</dt><dd>{formatConsentDate(document.familyIdentityVerification.verifiedAt)}</dd>
      </dl></div> : <p className={styles.absent}>Family member identity has not been verified.</p>}
    </section> : null}

    {document.status === ConsentDocumentStatus.WITHDRAWN && document.withdrawal ? <section className={styles.section}><h3>Withdrawal</h3>
      <div className={styles.evidenceRow}><dl>
        <dt>Withdrawn by</dt><dd>{document.withdrawal.withdrawnByName}{document.withdrawal.withdrawnByPosition ? ` (${document.withdrawal.withdrawnByPosition})` : ""}</dd>
        <dt>Withdrawn at</dt><dd>{formatConsentDate(document.withdrawal.withdrawnAt)}</dd>
        <dt>Reason</dt><dd>{document.withdrawal.reason}</dd>
        {linkedRequest ? <><dt>Family request</dt><dd>{requestByline(linkedRequest)}. {linkedRequest.reason}</dd></> : null}
      </dl></div>
    </section> : null}

    {resolvedRequests.length ? <section className={styles.section}><h3>Withdrawal requests</h3>
      <ul className={styles.evidenceList}>{resolvedRequests.map((request) => <li key={request.id} className={styles.evidenceRow}>
        <div><strong>{request.requestedByName}</strong><span className={styles.requestStatus}>{request.status === ConsentWithdrawalRequestStatus.PROCESSED ? "Processed" : "Cancelled"}</span></div>
        <dl>
          <dt>Requested</dt><dd>{requestByline(request)}</dd>
          <dt>Reason</dt><dd>{request.reason}</dd>
          {request.resolvedByName || request.resolvedAt ? <><dt>Resolved</dt><dd>{[request.resolvedByName, request.resolvedAt ? formatConsentDate(request.resolvedAt) : ""].filter(Boolean).join(" on ")}</dd></> : null}
          {request.resolutionNote ? <><dt>Note</dt><dd>{request.resolutionNote}</dd></> : null}
        </dl>
      </li>)}</ul>
    </section> : null}

    <section className={styles.section}><h3>Audit timeline</h3>
      {document.auditEvents?.length ? <div className={styles.timelineContainer}><ol className={styles.timeline}>{document.auditEvents.map((event, index) => <AuditEvidence key={`${event.type}-${event.occurredAt}-${index}`} event={event}/>)}</ol></div> : <p className={styles.absent}>No audit events are available.</p>}
    </section>

    <Modal open={withdrawalModal} close={() => { if (!withdrawing) setWithdrawalModal(false); }} maxWidth={560}>
      <div className={styles.withdrawalModal}>
        <h3>Withdraw consent</h3>
        <p className={styles.withdrawalWarning}>Withdrawing is permanent. The final PDF will be regenerated to record the withdrawal.</p>
        <TextInput
          id="consent-withdrawal-text"
          label="Reason"
          value={withdrawalText}
          multiline
          maxLength={WITHDRAWAL_TEXT_MAX}
          required
          disabled={withdrawing}
          handleChange={(event) => setWithdrawalText(event.target.value.slice(0, WITHDRAWAL_TEXT_MAX))}
        />
        {withdrawalError ? <p className={styles.pdfError} role="alert">{withdrawalError}</p> : null}
        <div className={styles.withdrawalModalActions}>
          <Button variant="outlined" className={styles.actionBtn} onClick={() => setWithdrawalModal(false)} disabled={withdrawing}>Cancel</Button>
          <Button className={styles.actionBtn} onClick={submitWithdrawal} disabled={withdrawing || !withdrawalText.trim()} isLoading={withdrawing}>Withdraw consent</Button>
        </div>
      </div>
    </Modal>
  </div>;
};
